import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, FileSpreadsheet, Printer, RefreshCw } from "lucide-react";
import { exportReportToExcel } from "@/pages/reports/shared/reportExportExcel";
import { showToast } from "@/redux/Toast/actions";
import { useDispatch } from "react-redux";
import ReportAnimatedTitle from "./ReportAnimatedTitle";
import {
    reportHeaderBackBtn,
    reportHeaderBreadcrumb,
    reportHeaderExportBtn,
    reportHeaderIconBtn,
    reportHeaderIconWrap,
    reportHeaderPrintBtn,
    reportMutedText,
    reportShellTopBar,
    reportShellTopBarCompact,
} from "./reportTheme";

const ReportPageHeader = ({
    title,
    breadcrumb,
    hubPath,
    subtitle,
    icon: Icon = BarChart3,
    onRefresh,
    exportSheets,
    exportFileName,
    showExport = true,
    showPrint = true,
    embedded = true,
    compact = false,
}) => {
    const topBarClass = compact ? reportShellTopBarCompact : reportShellTopBar;
    const dispatch = useDispatch();
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        if (!exportSheets?.length) {
            dispatch(showToast("No data to export", "error"));
            return;
        }
        setExporting(true);
        try {
            await exportReportToExcel({
                fileName: exportFileName || title,
                sheets: exportSheets,
            });
            dispatch(showToast("Report exported to Excel", "success"));
        } catch (e) {
            if (e?.message === "NO_DATA") {
                dispatch(showToast("No data to export", "error"));
            } else {
                dispatch(showToast("Export failed", "error"));
            }
        } finally {
            setExporting(false);
        }
    };

    const topBar = (
        <div className={embedded ? topBarClass : "flex flex-wrap items-center justify-between gap-3"}>
            <div className="flex min-w-0 items-center gap-3">
                {hubPath && (
                    <Link to={hubPath} title="Back to reports" className={reportHeaderBackBtn}>
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                )}
                <div className={reportHeaderIconWrap}>
                    <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                    {breadcrumb && <p className={reportHeaderBreadcrumb}>{breadcrumb}</p>}
                    <ReportAnimatedTitle title={title} />
                    {subtitle && (
                        <p className={`mt-0.5 text-[12px] hidden sm:block truncate max-w-md lg:max-w-xl ${reportMutedText}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-end">
                {onRefresh && (
                    <button type="button" onClick={onRefresh} title="Refresh" className={reportHeaderIconBtn}>
                        <RefreshCw className="h-4 w-4" />
                    </button>
                )}
                {showExport && (
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting}
                        className={reportHeaderExportBtn}
                    >
                        <FileSpreadsheet className="h-4 w-4" />
                        {exporting ? "Exporting…" : "Export Excel"}
                    </button>
                )}
                {showPrint && (
                    <button type="button" onClick={() => window.print()} className={reportHeaderPrintBtn}>
                        <Printer className="h-4 w-4" />
                        Print
                    </button>
                )}
            </div>
        </div>
    );

    return topBar;
};

export default ReportPageHeader;
